import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Container,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Zoom,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { SearchAutocomplete } from './SearchHistory';
import LanguageSwitcher from './LanguageSwitcher';
import { Helmet } from 'react-helmet-async';

const Layout = ({ children }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { cart, cartAnimation } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { storeConfig, categories: dbCategories, shippingConfig } = useStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileNav, setMobileNav] = useState(0);

  const isPlatformPreview = sessionStorage.getItem('platform_preview_mode') === 'true';
  const previewSlug = sessionStorage.getItem('preview_tenant_slug');

  const userMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname === '/') setMobileNav(0);
    else if (location.pathname.startsWith('/products')) setMobileNav(1);
    else if (location.pathname === '/cart') setMobileNav(2);
    else if (location.pathname === '/member' || location.pathname === '/login') setMobileNav(3);
  }, [location.pathname]);

  const categories = useMemo(() => {
    return (dbCategories || []).map((c) => ({
      id: c.id,
      name: c.name,
      path: `/products?category=${encodeURIComponent(String(c.id))}`,
    }));
  }, [dbCategories]);

  const freeShip = shippingConfig?.free_shipping_threshold ?? 0;
  const topBarText =
    freeShip > 0
      ? t('topbar.free_shipping', { threshold: Number(freeShip).toLocaleString() })
      : t('topbar.shipping_methods');

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/');
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Organization Schema — 動態租戶版
  const organizationSchema = storeConfig?.store_name ? {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: storeConfig.store_name,
    url: window.location.origin,
    ...(storeConfig.logo_url && {
      logo: storeConfig.logo_url.startsWith('http')
        ? storeConfig.logo_url
        : `${window.location.origin}${storeConfig.logo_url}`
    }),
    ...(storeConfig.description && { description: storeConfig.description }),
    ...((storeConfig.phone || storeConfig.email) && {
      contactPoint: {
        '@type': 'ContactPoint',
        ...(storeConfig.phone && { telephone: storeConfig.phone }),
        ...(storeConfig.email && { email: storeConfig.email }),
        contactType: 'customer service'
      }
    }),
    sameAs: [
      storeConfig.facebook_url,
      storeConfig.instagram_url,
      storeConfig.line_url
    ].filter(Boolean)
  } : null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {organizationSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
          </script>
        </Helmet>
      )}

      {isPlatformPreview && (
        <Box sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', py: 1, px: 2, textAlign: 'center', zIndex: 1300, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {t('platform.previewing_store', { defaultValue: `正在預覽商家：${storeConfig?.store_name || previewSlug}` })}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="inherit"
            sx={{ color: 'warning.main', fontWeight: 'bold' }}
            onClick={() => {
              sessionStorage.removeItem('platform_preview_mode');
              sessionStorage.removeItem('preview_tenant_slug');
              window.location.href = '/platform/tenants';
            }}
          >
            {t('platform.stop_preview', { defaultValue: '結束預覽' })}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          background: 'linear-gradient(90deg, #0F766E 0%, #14B8A6 60%, #E76F51 100%)',
          color: 'white',
          py: 0.75,
          textAlign: 'center',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {topBarText}
      </Box>

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.86)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 2.5,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              minWidth: 0,
            }}
          >
            {storeConfig?.logo_url ? (
              <Box
                component="img"
                src={storeConfig.logo_url}
                alt={storeConfig?.store_name || 'Store'}
                sx={{ height: 40, width: 'auto', display: { xs: 'none', sm: 'block' } }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <StorefrontIcon sx={{ color: 'primary.main' }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: 120, sm: 220 },
              }}
            >
              {storeConfig?.store_name || t('misc.my_store')}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, px: { xs: 0.5, sm: 2 }, maxWidth: 560, mx: 'auto' }}>
            <SearchAutocomplete onSearch={(kw) => navigate(`/products?search=${encodeURIComponent(kw)}`)} placeholder={t('search.placeholder')} />
          </Box>

          {!isMobile && (
            <>
              {isAuthenticated ? (
                <>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main' }}>
                      {user?.name?.charAt(0) || <PersonIcon fontSize="small" />}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={userMenuOpen}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography fontWeight={900}>{user?.name || t('nav.member')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user?.phone || user?.email || ''}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { setAnchorEl(null); navigate('/member'); }}>
                      <AccountCircleIcon sx={{ mr: 1.5 }} fontSize="small" />
                      {t('nav.member_center')}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                      <LogoutIcon sx={{ mr: 1.5 }} fontSize="small" />
                      {t('nav.logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <MenuItem component={RouterLink} to="/login" sx={{ borderRadius: 2 }}>
                  <LoginIcon sx={{ mr: 1.5 }} fontSize="small" />
                  {t('nav.login')}
                </MenuItem>
              )}
            </>
          )}

          <LanguageSwitcher />

          <IconButton
            onClick={() => navigate('/cart')}
            sx={{
              ml: 0.5,
              animation: cartAnimation ? 'cartBounce 0.6s ease' : 'none',
              '@keyframes cartBounce': {
                '0%': { transform: 'scale(1)' },
                '25%': { transform: 'scale(1.25)' },
                '50%': { transform: 'scale(0.95)' },
                '75%': { transform: 'scale(1.12)' },
                '100%': { transform: 'scale(1)' },
              },
            }}
          >
            <Badge badgeContent={cart?.itemCount || 0} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            borderTopRightRadius: 18,
            borderBottomRightRadius: 18,
            backgroundImage: 'linear-gradient(180deg, rgba(15,118,110,0.05), rgba(255,255,255,0.98) 20%)',
          },
        }}
      >
        <Box sx={{ p: 2.2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={900}>{t('nav.menu')}</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ py: 1 }}>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/'); }} sx={{ mx: 1, borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon /></ListItemIcon>
            <ListItemText primary={t('nav.home')} primaryTypographyProps={{ fontWeight: 800 }} />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/products'); }} sx={{ mx: 1, borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><CategoryIcon /></ListItemIcon>
            <ListItemText primary={t('nav.all_products')} primaryTypographyProps={{ fontWeight: 800 }} />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/shopping-guide'); }} sx={{ mx: 1, borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><HelpOutlineIcon /></ListItemIcon>
            <ListItemText primary={t('nav.shopping_guide')} primaryTypographyProps={{ fontWeight: 800 }} />
          </ListItemButton>
        </List>

        <Divider sx={{ my: 1 }} />

        <Typography variant="caption" sx={{ px: 2.2, color: 'text.secondary', fontWeight: 900 }}>
          {t('nav.categories')}
        </Typography>
        <List sx={{ py: 0, maxHeight: '55vh', overflowY: 'auto' }}>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/products'); }} sx={{ mx: 1, borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><CategoryIcon /></ListItemIcon>
            <ListItemText primary={t('nav.all')} primaryTypographyProps={{ fontWeight: 700 }} />
          </ListItemButton>
          {categories.map((c) => (
            <ListItemButton key={c.id} onClick={() => { setDrawerOpen(false); navigate(c.path); }} sx={{ mx: 1, borderRadius: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}><CategoryIcon /></ListItemIcon>
              <ListItemText primary={c.name} primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        <List sx={{ py: 0 }}>
          {isAuthenticated ? (
            <>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/member'); }} sx={{ mx: 1, borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}><AccountCircleIcon /></ListItemIcon>
                <ListItemText primary={t('nav.member_center')} primaryTypographyProps={{ fontWeight: 800 }} />
              </ListItemButton>
              <ListItemButton onClick={() => { setDrawerOpen(false); handleLogout(); }} sx={{ mx: 1, borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
                <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontWeight: 800, color: 'error.main' }} />
              </ListItemButton>
            </>
          ) : (
            <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/login'); }} sx={{ mx: 1, borderRadius: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}><LoginIcon /></ListItemIcon>
              <ListItemText primary={t('nav.login')} primaryTypographyProps={{ fontWeight: 800 }} />
            </ListItemButton>
          )}
        </List>
      </Drawer>

      <Box component="main" sx={{ flex: 1, pb: isMobile ? 8 : 0 }}>
        {children}
      </Box>

      <Zoom in={showScrollTop}>
        <Fab
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 86 : 24,
            right: 24,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, borderTop: '1px solid', borderColor: 'divider' }} elevation={6}>
          <BottomNavigation value={mobileNav} onChange={(_, v) => setMobileNav(v)} showLabels>
            <BottomNavigationAction label={t('nav.home')} icon={<HomeIcon />} onClick={() => navigate('/')} />
            <BottomNavigationAction label={t('nav.products')} icon={<CategoryIcon />} onClick={() => navigate('/products')} />
            <BottomNavigationAction
              label={t('nav.cart')}
              icon={
                <Badge badgeContent={cart?.itemCount || 0} color="error">
                  <ShoppingCartIcon />
                </Badge>
              }
              onClick={() => navigate('/cart')}
            />
            <BottomNavigationAction
              label={t('nav.member')}
              icon={<PersonIcon />}
              onClick={() => navigate(isAuthenticated ? '/member' : '/login')}
            />
          </BottomNavigation>
        </Paper>
      )}

      {!isMobile && (
        <Box
          component="footer"
          sx={{
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 3,
            backgroundImage: 'radial-gradient(circle at 0% 0%, rgba(20,184,166,0.08), transparent 40%)',
          }}
        >
          <Container maxWidth="lg" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 240 }}>
              <Typography fontWeight={900}>{storeConfig?.store_name || t('misc.my_store')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {storeConfig?.slogan || t('footer.slogan_default')}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 240 }}>
              <Typography fontWeight={900}>{t('footer.contact')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('footer.phone', { phone: storeConfig?.phone || '-' })}
                <br />
                {t('footer.email', { email: storeConfig?.email || '-' })}
              </Typography>
            </Box>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default Layout;
